import React, { useState } from 'react';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import '../../css/components/TruckLoanApproval.css';

import { useUser } from '../../hooks/useUser';
import { Message } from '../../utils/Message';
import { DataTable } from '../DataTable';
import { roleConfig } from '../../constants/roleConfig';
import { TruckLoanApplicationDataColumns } from '../../constants';
import { TruckLoanApplicationCheckModal } from './TruckLoanApplicationCheckModal';
import RoleGuard from '../RoleGuard/RoleGuard';
import { SearchArea } from './SearchArea';

import {
    getTruckLoanApplications,
    approveTruckLoanApplication,
    rejectTruckLoanApplication,
    approveCancelRequest,
    approveReturn
} from '../../service/api/TruckLoanApplication/apiTruckLoanApplication';
import { updateDeliverOrder } from '../../service/api/StaffOrder/apiStaffOrder';

const TruckLoanApproval = () => {
    const { id: userId, name: userName } = useUser();
    const [showSearchArea, setShowSearchArea] = useState(false); // 搜尋區域顯示開關
    const [applications, setApplications] = useState([]);
    const [dataCount, setDataCount] = useState(0); // 放資料總數用
    const [loading, setLoading] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState(''); // 'approve' or 'reject'
    const [rejectReason, setRejectReason] = useState('');
    const [filter, setFilter] = useState('pending'); // 'pending', 'repentant', 'approved', 'rejected', 'returned', 'all'
    // 搜尋條件物件
    const [searchForm, setSearchForm] = useState({
        id: '',
        applicantDate: '',
        deliverId: '',
        deliverOrderId: '',
        startAt: '',
        status: '',
    });

    // 根據篩選條件取得資料
    const fetchByFilter = async (filterType) => {
        setLoading(true);
        try {
            let response;
            if (filterType === 'pending' || filterType === 'repentant') {
                response = await getTruckLoanApplications({ status: ['pending', 'repentant'], page: 1, limit: 10 });
                setSearchForm(prev => ({ ...prev, status: 'pending,repentant' }));
            } else if (filterType === 'approved') {
                response = await getTruckLoanApplications({ status: 'approved', page: 1, limit: 10 });
                setSearchForm(prev => ({ ...prev, status: 'approved' }));
            } else if (filterType === 'rejected') {
                response = await getTruckLoanApplications({ status: 'rejected', page: 1, limit: 10 });
                setSearchForm(prev => ({ ...prev, status: 'rejected' }));
            } else if (filterType === 'returned') {
                response = await getTruckLoanApplications({ status: 'returned', page: 1, limit: 10 });
                setSearchForm(prev => ({ ...prev, status: 'returned' }));
            } else if (filterType === 'cancelled') {
                response = await getTruckLoanApplications({ status: 'cancelled', page: 1, limit: 10 });
                setSearchForm(prev => ({ ...prev, status: 'cancelled' }));
            } else if (filterType === 'voided') {
                response = await getTruckLoanApplications({ status: 'voided', page: 1, limit: 10 });
                setSearchForm(prev => ({ ...prev, status: 'voided' }));
            } else {
                response = await getTruckLoanApplications({ page: 1, limit: 10 });
                setSearchForm(prev => ({ ...prev, status: '' }));
            }

            if (response?.status === 200 && response.data?.status === 1) {
                console.log(response.data);
                setDataCount(response.data.count)
                setApplications(response.data.result || []);
            } else {
                setApplications([]);
            }
        } catch (error) {
            console.error('取得借車申請失敗:', error);
            Message('取得借車申請失敗', 'error');
        } finally {
            setLoading(false);
        }
    };

    // 切換篩選並取得資料
    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        fetchByFilter(newFilter);
    };

    // 顯示的申請列表（已經從 API 篩選過了）
    const filteredApplications = applications;

    const columns = [
        ...TruckLoanApplicationDataColumns,
        {
            name: '狀態',
            key: 'status',
            selector: (row) => getStatusBadge(row.status),
            cell: (row) => getStatusBadge(row.status),
        },
        {
            name: '操作',
            key: 'actions',
            cell: (row) => {
                // 取消申請（待審核且標記為取消申請）
                if (row.status === 'pending' && row.isCancelRequest) {
                    return (
                        <div className="btn-group-vertical">
                            <button className="btn btn-sm btn-success mb-1" onClick={() => handleApproveCancelClick(row)}>
                                <i className="fas fa-check"></i> 核准撤銷
                            </button>
                            <button className="btn btn-sm btn-danger" onClick={() => handleRejectCancelClick(row)}>
                                <i className="fas fa-times"></i> 駁回撤銷
                            </button>
                        </div>
                    );
                }

                // 修改申請（已審核但標記為修改申請）
                if (row.status === 'repentant') {
                    return (
                        <div className="btn-group-vertical">
                            <button className="btn btn-sm btn-success mb-1" onClick={() => handleApproveClick(row)}>
                                <i className="fas fa-check"></i> 核准修改
                            </button>
                            <button className="btn btn-sm btn-danger" onClick={() => handleRejectClick(row)}>
                                <i className="fas fa-times"></i> 駁回修改
                            </button>
                        </div>
                    );
                }

                // 還車申請
                if (!row.actualCheckoutAt && !row.actualReturnAt && !row.preCheckStatus && !row.postCheckStatus && row.status === 'pending') {
                    return (
                        <div className="btn-group-vertical">
                            <button className="btn btn-sm btn-success mb-1" onClick={() => handleApproveClick(row)}>
                                <i className="fas fa-check"></i> 核准
                            </button>
                            <button className="btn btn-sm btn-danger" onClick={() => handleRejectClick(row)}>
                                <i className="fas fa-times"></i> 駁回
                            </button>
                        </div>
                    );
                }

                if (row.actualCheckoutAt && row.actualReturnAt && row.preCheckStatus && row.postCheckStatus && row.status === 'pending') {
                    return (
                        <div className="btn-group-vertical">
                            <button className="btn btn-sm btn-success mb-1" onClick={() => handleApproveClick(row)}>
                                <i className="fas fa-check"></i> 核准還車
                            </button>
                            <button className="btn btn-sm btn-danger" onClick={() => handleRejectClick(row)}>
                                <i className="fas fa-times"></i> 駁回還車
                            </button>
                        </div>
                    );
                }

                return (
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                            setSelectedApp(row);
                            setModalType('view');
                            setShowModal(true);
                        }}
                    >
                        <i className="fas fa-eye"></i> 查看
                    </button>
                );
            },
        },
    ];

    // 打開核准確認
    const handleApproveClick = (app) => {
        console.log(app)
        setSelectedApp(app);
        setModalType('approve');
        setShowModal(true);
    };

    // 打開駁回確認
    const handleRejectClick = (app) => {
        setSelectedApp(app);
        setModalType('reject');
        setRejectReason('');
        setShowModal(true);
    };

    // 打開核准取消確認
    const handleApproveCancelClick = (app) => {
        setSelectedApp(app);
        setModalType('approveCancelRequest');
        setShowModal(true);
    };

    // 打開駁回取消確認
    const handleRejectCancelClick = (app) => {
        setSelectedApp(app);
        setModalType('rejectCancel');
        setRejectReason('');
        setShowModal(true);
    };

    // 確認核准
    const handleApprove = async () => {
        if (!selectedApp) return;

        let approveType = null;

        if (selectedApp.actualCheckoutAt && selectedApp.actualReturnAt && selectedApp.preCheckStatus && selectedApp.postCheckStatus) {
            approveType = 'approveReturn';
        }

        try {
            let response = approveType ? await approveReturn({ id: selectedApp.id, custodian: userName }) :
                await approveTruckLoanApplication({ applicationId: selectedApp.id, managerId: userId, managerName: userName, deliverOrderId: selectedApp.deliverOrderId });
            if (response?.status === 200 && response.data?.status === 1) {
                if (selectedApp.status === 'repentant') {
                    await updateDeliverOrder({
                        ...selectedApp.deliverOrder,
                        company: selectedApp.deliver.company.name,
                        name: selectedApp.applicantName,
                        category: selectedApp.category,
                        plate: selectedApp.vehicle.plate,
                        isReturned: selectedApp.is_returned,
                        furnitureCount: selectedApp.deliverOrder.furniture_count,
                        phone: selectedApp.phone,
                        usedAt: selectedApp.startAt,
                        finishAt: selectedApp.finishAt,
                        returnAt: selectedApp.endAt,
                    });
                }
                Message('已核准借車申請', 'success');
                setShowModal(false);
                fetchByFilter(filter); // 重新載入目前篩選
            } else {
                Message(response?.data?.message || '核准失敗', 'error');
            }
        } catch (error) {
            console.error('核准失敗:', error);
            Message('核准失敗', 'error');
        }
    };

    // 確認駁回
    const handleReject = async () => {
        if (!selectedApp) return;
        if (!rejectReason.trim()) {
            Message('請輸入駁回原因', 'warning');
            return;
        }

        try {
            const response = await rejectTruckLoanApplication(
                {
                    applicationId: selectedApp.id,
                    managerId: userId,
                    managerName: userName,
                    reason: rejectReason,
                    deliverOrderId: selectedApp.deliverOrderId
                });
            if (response?.status === 200 && response.data?.status === 1) {
                Message('已駁回借車申請', 'success');
                setShowModal(false);
                fetchByFilter(filter); // 重新載入目前篩選
            } else {
                Message(response?.data?.message || '駁回失敗', 'error');
            }
        } catch (error) {
            console.error('駁回失敗:', error);
            Message('駁回失敗', 'error');
        }
    };

    // 確認核准取消申請
    const handleApproveCancelRequest = async () => {
        if (!selectedApp) return;

        try {
            const response = await approveCancelRequest({
                applicationId: selectedApp.id,
                managerId: userId,
                managerName: userName,
                deliverOrderId: selectedApp.deliverOrderId
            });

            if (response?.status === 200 && response.data?.status === 1) {
                Message('已核准取消申請', 'success');
                setShowModal(false);
                fetchByFilter(filter);
            } else {
                Message(response?.data?.message || '核准失敗', 'error');
            }
        } catch (error) {
            console.error('核准取消申請失敗:', error);
            Message('核准取消申請失敗', 'error');
        }
    };

    // 確認駁回取消申請
    const handleRejectCancelRequest = async () => {
        if (!selectedApp) return;

        try {
            const response = await rejectTruckLoanApplication({
                applicationId: selectedApp.id,
                managerId: userId,
                managerName: userName,
                reason: '駁回取消申請',
                deliverOrderId: selectedApp.deliverOrderId
            });

            if (response?.status === 200 && response.data?.status === 1) {
                Message('已駁回取消申請', 'success');
                setShowModal(false);
                fetchByFilter(filter);
            } else {
                Message(response?.data?.message || '駁回失敗', 'error');
            }
        } catch (error) {
            console.error('駁回取消申請失敗:', error);
            Message('駁回取消申請失敗', 'error');
        }
    };

    // 狀態顯示
    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <span className="badge bg-warning text-dark">待審核</span>;
            case 'repentant':
                return <span className="badge bg-warning text-dark">待審核</span>;
            case 'approved':
                return <span className="badge bg-success">已核准</span>;
            case 'rejected':
                return <span className="badge bg-danger">已駁回</span>;
            case 'returned':
                return <span className="badge bg-info">已歸還</span>;
            case 'cancelled':
                return <span className="badge bg-dark text-light">已取消</span>;
            case 'voided':
                return <span className="badge bg-secondary">作廢</span>;
            default:
                return <span className="badge bg-secondary">{status}</span>;
        }
    };

    const handleSearch = async ({ page = 1, limit = 10, customParams = null }) => {
        console.log(123)

        setLoading(true);
        try {
            const params = {
                page: page,
                limit: limit,
                ...(customParams || searchForm)
            };
            console.log(params);
            const response = await getTruckLoanApplications(params);
            if (response?.status === 200 && response.data?.status === 1) {
                console.log(response.data);
                setDataCount(response.data.count || 0)
                setApplications(response.data.result || []);
            } else {
                setApplications([]);
            }
        } catch (error) {
            Message('搜尋失敗: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="truck-loan-approval">
            <div className="approval-header">
                <h2>借車申請審核</h2>
                <p className="text-muted">審核順康公司人員的車輛借用申請</p>
            </div>

            {/* 搜尋區域 */}
            {showSearchArea && (
                <div className='filter-buttons'>
                    <SearchArea
                        searchForm={searchForm}
                        setSearchForm={setSearchForm}
                        loading={loading}
                        onSearch={handleSearch}
                        onReset={() => {
                            setSearchForm({
                                id: '',
                                applicantDate: '',
                                deliverId: '',
                                deliverOrderId: '',
                                startAt: '',
                                status: '',
                            });
                            // setOrdersData([]);
                        }}
                    />
                </div>
            )}


            {/* 篩選按鈕 */}
            <div className="filter-buttons mb-3">
                <RoleGuard requiredRole={roleConfig['協理']}>
                    <button
                        className={`btn ${filter === 'pending' ? 'btn-primary' : 'btn-outline-primary'} me-2`}
                        onClick={() => handleFilterChange('pending')}
                    >
                        待審核
                    </button>
                </RoleGuard>
                <button
                    className={`btn ${filter === 'approved' ? 'btn-success' : 'btn-outline-success'} me-2`}
                    onClick={() => handleFilterChange('approved')}
                >
                    已核准
                </button>
                <button
                    className={`btn ${filter === 'rejected' ? 'btn-danger' : 'btn-outline-danger'} me-2`}
                    onClick={() => handleFilterChange('rejected')}
                >
                    已駁回
                </button>
                <button
                    className={`btn ${filter === 'returned' ? 'btn-info' : 'btn-outline-info'} me-2`}
                    onClick={() => handleFilterChange('returned')}
                >
                    已歸還
                </button>
                <button
                    className={`btn ${filter === 'cancelled' ? 'btn-dark' : 'btn-outline-dark'} me-2`}
                    onClick={() => handleFilterChange('cancelled')}
                >
                    已取消
                </button>
                <button
                    className={`btn ${filter === 'voided' ? 'btn-secondary' : 'btn-outline-secondary'} me-2`}
                    onClick={() => handleFilterChange('voided')}
                >
                    已作廢
                </button>
                <button
                    className={`btn ${filter === 'all' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => handleFilterChange('all')}
                >
                    全部
                </button>
                <button
                    className="btn btn-outline-dark ms-3"
                    onClick={() => fetchByFilter(filter)}
                    disabled={loading}
                >
                    <i className="fas fa-sync-alt"></i> 重新整理
                </button>
                {/* 欄位篩選面板 */}
                <div className="d-flex gap-2">
                    <button
                        onClick={() => setShowSearchArea(!showSearchArea)}
                        className="btn btn-outline-secondary btn-sm fw-bold"
                        title={showSearchArea ? "隱藏搜尋" : "顯示搜尋"}
                    >
                        <FontAwesomeIcon icon={showSearchArea ? faChevronUp : faChevronDown} className="me-2" />
                        {showSearchArea ? '隱藏搜尋' : '顯示搜尋'}
                    </button>
                </div>
            </div>

            {/* 提示文字 */}
            {applications.length === 0 && !loading && (
                <div className="alert alert-secondary">
                    無資料，點擊上方按鈕查詢借車申請記錄
                </div>
            )}

            {/* 申請列表 */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">載入中...</span>
                    </div>
                </div>
            ) : applications.length > 0 && filteredApplications.length === 0 ? (
                <div className="alert alert-info">
                    {filter === 'pending' ? '目前沒有待審核的借車申請' : '沒有符合條件的借車申請'}
                </div>
            ) : filteredApplications.length > 0 && (
                <DataTable
                    columns={columns}
                    data={filteredApplications}
                    responsive={true}
                    pagination={true}
                    paginationComponentOptions={{
                        rowsPerPageText: '每頁筆數',
                        rangeSeparatorText: '總共',
                    }}
                    noDataComponent={<div className="alert alert-secondary">無資料，點擊上方按鈕查詢借車申請記錄</div>}
                    paginationMode={{
                        api: getTruckLoanApplications,
                        count: dataCount,
                        searchFields: searchForm
                    }}
                />
            )}

            {/* 確認 Modal */}
            {showModal && selectedApp && (
                <TruckLoanApplicationCheckModal
                    getStatusBadge={getStatusBadge}
                    selectedApp={selectedApp}
                    modalType={modalType}
                    setShowModal={setShowModal}
                    rejectReason={rejectReason}
                    setRejectReason={setRejectReason}
                    handleApprove={handleApprove}
                    handleReject={handleReject}
                    handleApproveCancelRequest={handleApproveCancelRequest}
                    handleRejectCancelRequest={handleRejectCancelRequest}
                />
            )}
        </div>
    );
};

export { TruckLoanApproval };
